import { FunnelId } from 'constants/funnel';
import clsx from 'clsx';
import ButtonComp from 'components/Qds/Button';
import ExternalFunnelButton from 'components/Shared/ExternalFunnel/ExternalFunnelButton';
import Link from 'components/ui/Link';
import { useAnalytics } from 'hooks/useAnalytics';
import type { ReactNode } from 'react';
import type { InspectorPropsType } from 'hooks/useLivePreview';
import type {
  IBlockButtonFields,
  IBlockFunnel,
} from 'types/contentful/generated';

type Props = Omit<IBlockButtonFields, 'link' | 'target'> & {
  link?: string;
  onClick?: () => void;
  parentHasEventTracking?: boolean;
  target?: string;
  inspectorProps?: InspectorPropsType;
  funnel?: IBlockFunnel;
  className?: string;
  icon?: ReactNode;
};

const Button = ({
  text,
  link,
  target,
  position,
  theme,
  triggerLeadForm,
  onClick,
  blockId,
  parentHasEventTracking = false,
  inspectorProps,
  funnel,
  className,
  icon,
}: Props): JSX.Element => {
  const { trackEvent } = useAnalytics();

  const alignment = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  const handleClick = (): void => {
    if (!parentHasEventTracking) {
      trackEvent({
        category: 'CTA',
        action: 'CTA|Click',
        label: `CTA|Block: Button|${blockId}|${text}`,
      });
    }

    onClick?.();
  };

  const renderButton = (onClick: () => void): JSX.Element => {
    return (
      <ButtonComp
        className="!inline-flex !items-center !justify-center !font-adelle !text-base"
        variant={theme === 'light' ? 'primary' : 'tertiary'}
        fullWidth="mobile"
        onClick={onClick}
        icon={icon}
      >
        <span className="mr-2" {...inspectorProps?.({ fieldId: 'text' })}>
          {text}
        </span>
      </ButtonComp>
    );
  };

  return (
    <div
      className={clsx(
        'flex max-w-screen-xl mx-auto',
        alignment[position],
        className,
      )}
    >
      {link && !triggerLeadForm && (
        <Link href={link} passHref>
          <ButtonComp
            className="!inline-flex !items-center !justify-center !font-adelle"
            variant={theme === 'light' ? 'primary' : 'tertiary'}
            fullWidth="mobile"
            isLink
            target={target}
            onClick={onClick}
            icon={icon}
          >
            <span className="mr-2" {...inspectorProps?.({ fieldId: 'text' })}>
              {text}
            </span>
          </ButtonComp>
        </Link>
      )}
      {!link && !triggerLeadForm && <>{renderButton(handleClick)}</>}
      {triggerLeadForm && (
        <ExternalFunnelButton
          funnelId={funnel ? funnel.sys.id : FunnelId.CONTACT_FORM}
          renderButton={(openFunnel) => {
            return renderButton(() => {
              handleClick();
              openFunnel();
            });
          }}
        />
      )}
    </div>
  );
};

export default Button;
